<template>
  <Card>
    <CardHeader>
      <CardTitle>Assets</CardTitle>
    </CardHeader>
    <CardContent class="flex flex-col gap-3">
      <p v-if="!isValidBusinessSystemId" class="text-sm text-destructive">
        Invalid business system id.
      </p>
      <template v-else>
        <div class="flex items-center gap-3">
          <UploadButton
            :multiple="true"
            :disabled="isCreatingAssets"
            label="Upload files"
            uploading-label="Uploading..."
            @uploaded="handleUploadedFiles"
            @error="handleUploadError"
          />
          <span v-if="isCreatingAssets" class="text-sm text-muted-foreground">
            Linking uploaded files to this business system...
          </span>
        </div>

        <p v-if="assetStore.isLoading" class="text-sm text-muted-foreground">
          Loading assets...
        </p>
        <p v-else-if="assets.length === 0" class="text-sm text-muted-foreground">
          No assets linked to this business system yet.
        </p>
        <UmisisAnimatedList v-else :items="assets" list-class="flex flex-col gap-2">
          <template #default="{ item: asset }">
            <BusinessSystemAssetItem
              :asset="asset"
              :is-deleting="isDeletingAsset(asset.id)"
              @delete="handleDeleteAsset"
            />
          </template>
        </UmisisAnimatedList>
      </template>
    </CardContent>
  </Card>
</template>

<script setup>
import UploadButton from '@Uploadable/components/UploadButton.vue'
import axios from 'axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import UmisisAnimatedList from '../../../../../../../../resources/js/components/umisis/generic/UmisisAnimatedList.vue'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import BusinessSystemAssetItem from '../../../components/business-systems-assets/BusinessSystemAssetItem.vue'
import { useBusinessSystemAssetStore } from '../../../stores/businessSystemAssets.js'

const route = useRoute()
const assetStore = useBusinessSystemAssetStore()
const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const isValidBusinessSystemId = computed(() => Number.isInteger(businessSystemId.value) && businessSystemId.value > 0)
const isCreatingAssets = ref(false)
const assets = computed(() => {
  return [...assetStore.items].sort((firstAsset, secondAsset) => Number(secondAsset.id) - Number(firstAsset.id))
})

const normalizeUploadedItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (payload && typeof payload === 'object') {
    return [payload]
  }

  return []
}

const extractUploadIds = (payload) => {
  return [...new Set(normalizeUploadedItems(payload)
    .map((item) => Number(item?.id ?? 0))
    .filter((uploadId) => Number.isInteger(uploadId) && uploadId > 0))]
}

const handleUploadedFiles = async (payload) => {
  if (!isValidBusinessSystemId.value) {
    toast.error('Invalid business system id.')
    return
  }

  const uploadIds = extractUploadIds(payload)

  if (uploadIds.length === 0) {
    toast.error('Upload succeeded but no uploaded file IDs were returned.')
    return
  }

  isCreatingAssets.value = true

  try {
    const { data } = await axios.post(`/api/v1/business-systems/${businessSystemId.value}/assets`, {
      upload_ids: uploadIds,
    })
    await assetStore.fetchItemsForBusinessSystem(businessSystemId.value)

    const createdCount = Number(data?.created_count ?? 0)

    if (createdCount > 0) {
      toast.success(`${createdCount} asset(s) linked successfully.`)
      return
    }

    toast.success('Selected file(s) were already linked.')
  } catch (error) {
    toast.error(error?.response?.data?.message ?? 'Unable to link uploaded files as assets.')
  } finally {
    isCreatingAssets.value = false
  }
}

const handleUploadError = (error) => {
  toast.error(error?.response?.data?.message ?? error?.message ?? 'Unable to upload selected file(s).')
}

const isDeletingAsset = (assetId) => {
  return assetStore.isLoadingItems.includes(Number(assetId))
}

const handleDeleteAsset = async (asset) => {
  try {
    await assetStore.deleteBusinessSystemAsset(businessSystemId.value, asset?.id)
    toast.success('Asset removed from business system.')
  } catch (error) {
    toast.error(error?.response?.data?.message ?? 'Unable to delete asset.')
  }
}

const loadAssets = async () => {
  if (!isValidBusinessSystemId.value) {
    assetStore.items = []
    return
  }

  try {
    await assetStore.fetchItemsForBusinessSystem(businessSystemId.value)
  } catch (error) {
    toast.error(error?.response?.data?.message ?? 'Unable to load assets.')
  }
}

watch(businessSystemId, async () => {
  await loadAssets()
})

onMounted(async () => {
  await loadAssets()
})
</script>
